import Loading from '@/components/Loading';
import Button from '@/components/UI/Button';
import SearchSelect from '@/components/UI/SearchSelect';
import { _notNil } from '@/littledash';
import { type UserApiId } from '@/model/User.model.ts';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import type { StudyApiId } from '@/model/Study.model';
import { type FC, type ReactNode, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  type StepFormPayloadProps,
  type StepRenderProps,
  type StudyUsersApi,
  type TaskSpecUser,
} from '../AddTask.model';
import styles from '../AddTask.module.scss';

const AddTaskAssigneesForm: FC<StepRenderProps> = ({
  onSubmit,
  onCancel,
  submitButtonText,
  cancelButtonText,
  studyApiId,
  studyCreationUsers,
  teamUsers,
}) => {
  const { handleSubmit: useFormHandleSubmit, formState } = useFormContext<StepFormPayloadProps>();

  return (
    <form
      className={styles['step-content']}
      onSubmit={useFormHandleSubmit(onSubmit)}
      data-test-component="AddTaskAssigneesForm"
      data-test-element="form"
    >
      {_notNil(studyApiId) ? (
        <StudyUsersProvider studyApiId={studyApiId}>
          {(studyUsers: StudyUsersApi) => <AssigneeMenu studyUsers={studyUsers} teamUsers={teamUsers ?? []} />}
        </StudyUsersProvider>
      ) : (
        <AssigneeMenu studyUsers={studyCreationUsers ?? []} teamUsers={teamUsers ?? []} />
      )}
      <div className="pa3 bt b--moon-gray">
        <Button submit testKey="taskSpecStepFormSubmit" disabled={!formState.isValid || formState.isSubmitting}>
          {submitButtonText}
        </Button>
        <Button plain className="ml2" onClick={onCancel}>
          {cancelButtonText}
        </Button>
      </div>
    </form>
  );
};

const StudyUsersProvider: FC<{ studyApiId: StudyApiId; children: (studyUsers: StudyUsersApi) => ReactNode }> = ({
  studyApiId,
  children,
}) => {
  const { response, loading } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyId}/users',
    path: { studyId: studyApiId },
    query: { perPage: -1, status: ['active'] },
  });

  if (loading) {
    return <Loading />;
  }

  const studyUsers = response?.type === 'success' ? response?.body?.data : {};

  return <>{children(studyUsers as StudyUsersApi)}</>;
};

const AssigneeMenu: FC<{ studyUsers: TaskSpecUser[]; teamUsers: TaskSpecUser[] }> = ({ studyUsers, teamUsers }) => {
  const { control } = useFormContext<StepFormPayloadProps>();

  const { sections, studyUserApiIds } = useMemo(() => {
    const { studyUserItems, studyUserApiIds } = studyUsers.reduce<{
      studyUserItems: Array<{ id: UserApiId; title: string }>;
      studyUserApiIds: Set<UserApiId>;
    }>(
      (acc, user) => {
        acc.studyUserItems.push({ id: user.api_id, title: user.name ?? user.email });
        acc.studyUserApiIds.add(user.api_id);
        return acc;
      },
      { studyUserItems: [], studyUserApiIds: new Set() }
    );
    const teamUserItems = teamUsers.reduce<Array<{ id: UserApiId; title: string }>>((acc, user) => {
      if (!studyUserApiIds.has(user.api_id)) {
        acc.push({ id: user.api_id, title: user.name ?? user.email });
      }
      return acc;
    }, []);
    const sections = [
      {
        title: 'Study Users',
        items: studyUserItems,
      },
    ];
    if (teamUserItems.length > 0) {
      sections.push({
        title: 'Team Users',
        items: teamUserItems,
      });
    }
    return { sections, studyUserApiIds };
  }, [teamUsers, studyUsers]);

  return (
    <div className={styles['add-task-select-menu-container']}>
      <Controller
        name="assignees"
        control={control}
        render={({ field }) => (
          <SearchSelect
            selected={field.value}
            onSelect={(event: { type: 'ADDED' | 'REMOVED'; value: UserApiId }) => {
              const update: Set<UserApiId> = new Set(field.value);
              switch (event.type) {
                case 'ADDED':
                  update.add(event.value);
                  break;
                case 'REMOVED':
                  update.delete(event.value);
                  break;
              }
              field.onChange([...update]);
            }}
            onSelectAll={() => field.onChange([...studyUserApiIds])}
            onClearSelection={() => field.onChange([])}
            sections={sections}
          />
        )}
      />
    </div>
  );
};

export default AddTaskAssigneesForm;
